import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IReportList } from 'src/app/core/interfaces/report-list.interface';
import { JsonFormData } from 'src/app/core/interfaces/report-parameter.interface';
import { IResponse } from 'src/app/core/interfaces/response.interface';
import { ReportModel } from 'src/app/core/models/report.model';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Store } from '@ngrx/store';
import { FileActionService } from './file-action.service';
import * as ReportActions from '../store/actions/report.actions';
import * as ReportSelectors from '../store/selectors/report.selectors';
import { first } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class ReportService {
  labelId: any;
  constructor(
    private http: HttpClient,
    private store: Store,
    private fileActionService: FileActionService
  ) { }

  downloadReport(reportId: number, language: string, parameters: JsonFormData) {
    let query = {};
    query = { reportId, language };
    return this.http.post<IResponse<string>>(
      `${environment.apiUrl}/reports/download`,
      parameters,
      {
        params: {
          ...query,
        },
      }
    );
  }

  downloadReports(action: string, reportId: number, language: string, parameters: any) {
    if (!reportId) {
      console.error('No report ID provided');
      return;
    }



    this.store.dispatch(
      ReportActions.downloadReport({
        payload: {
          reportId,
          language,
          parameters,
        },
      })
    );

    this.store
      .select(ReportSelectors.selectDownloadedReport)
      .pipe(first((report) => !!report))
      .subscribe(async (report) => {
        try {
          switch (action) {
            case 'browserSave':
              await this.fileActionService.browserSave('report', report);
              break;
            case 'browserPreview':
              await this.fileActionService.browserPreview(report);
              break;
          }
        } catch (error) {
          console.error('Error during report download:', error);
        } finally {
          this.store.dispatch(ReportActions.clearDownloadedReport());
        }
      });
  }

  reportMethod(parcelOrBuilding: string, label: string, operator: string, value: any) {
    this.http.get<any>('assets/report-parameters-el.json').subscribe((data) => {
      // Εξαγωγή των συγκεκριμένων παραμέτρων
      const action = 'browserPreview'; // Ή όποια άλλη ενέργεια θέλετε


      const isParcel = /γεωτεμαχια?/i.test(parcelOrBuilding);
      const spatialKey = isParcel ? 'parcels_spatial' : 'buildings_spatial';
      const reportId = isParcel ? 1 : 5;


      // Δημιουργία των παραμέτρων με τις 3 βασικές μεταβλητές
      const selectedParams = {
        addrs: data.addrs || [],
        spatial: data[spatialKey] || [],  // Χρήση του κατάλληλου array ανάλογα με τον τύπο
        perioxi: data.perioxi || []
      };



      if (label === 'καδηκ') {
        this.labelId = 1;
      } else if (label === 'καεκ') {
        this.labelId = 2;
      } else if (label === 'εμβαδον' || label === 'εμβαδο') {
        this.labelId = 3;
      } else if (label === 'ΤΚ') {
        selectedParams.addrs[1].value = value;
      }
      selectedParams.spatial[this.labelId].value = value;

      switch (operator) {
        case 'ισο':
        case '=':
          selectedParams.spatial[this.labelId].operator = '=';
          selectedParams.spatial[this.labelId].type = 'string';
          break;
        case 'διαφορετικο':
        case '<>':
          selectedParams.spatial[this.labelId].operator = '<>';
          selectedParams.spatial[this.labelId].type = 'string';
          break;
        case 'περιεχει':
          selectedParams.spatial[this.labelId].operator = 'contains';
          selectedParams.spatial[this.labelId].type = 'string';
          break;
        case 'δεν περιεχει':
          selectedParams.spatial[this.labelId].operator = 'not_contains';
          selectedParams.spatial[this.labelId].type = 'string';
          break;
        case 'αρχιζει':
          selectedParams.spatial[this.labelId].operator = 'starts_with';
          selectedParams.spatial[this.labelId].type = 'string';
          break;
        case 'τελειωνει':
          selectedParams.spatial[this.labelId].operator = 'ends_with';
          selectedParams.spatial[this.labelId].type = 'string';
          break;
        case 'μεγαλυτερο':
        case '>':
          selectedParams.spatial[this.labelId].operator = '>';
          selectedParams.spatial[this.labelId].type = 'float';
          break;
        case 'μεγαλυτερο ή ισο':
        case '>=':
          selectedParams.spatial[this.labelId].operator = '>=';
          selectedParams.spatial[this.labelId].type = 'float';
          break;
        case 'μικροτερο':
        case '<':
          selectedParams.spatial[this.labelId].operator = '<';
          selectedParams.spatial[this.labelId].type = 'float';
          break;
        case 'μικροτερο ή ισο':
        case '<=':
          selectedParams.spatial[this.labelId].operator = '<=';
          selectedParams.spatial[this.labelId].type = 'float';
          break;
        default:
          selectedParams.spatial[this.labelId].operator = 'contains';
          break;
      }


      this.downloadReports(action, reportId, 'el', selectedParams);
    });


  }

  createReport(report: ReportModel): Observable<null> {
    return this.http.post<null>(`${environment.apiUrl}/reports`, report);
  }

  readReports(): Observable<IResponse<IReportList[]>> {
    return this.http
      .get<IResponse<IReportList[]>>(`${environment.apiUrl}/reports`, {})
      .pipe(
        map((x) => {
          x.data = x.data.map((reportGroup) => ({
            ...reportGroup,
            expanded: true,
          }));
          return x;
        })
      );
  }

  readReportById(reportId: number): Observable<IResponse<ReportModel>> {
    return this.http.get<IResponse<ReportModel>>(
      `${environment.apiUrl}/reports/${reportId}`
    );
  }

  updateReport(report: ReportModel): Observable<null> {
    return this.http.put<null>(
      `${environment.apiUrl}/reports/${report.id}`,
      report
    );
  }

  deleteReport(reportId: number, version: number): Observable<null> {
    return this.http.delete<null>(
      `${environment.apiUrl}/reports/${reportId}/version/${version}`
    );
  }
}
